import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../AppContextDef";
import MainCard from "../MainCard";
import RemoteParticipantView from "../RemoteParticipantView";
import useIsMobile from "../../utils/useIsMobile";
import useIsTab from "../../utils/useIsTab";
import { usePubSub } from "@videosdk.live/react-sdk";
import { APP_FONT_STACK } from "../../utils/common";

const QnaSection = ({ formattedTime }) => {
    const { t } = useTranslation();
    const { allPubsubData, HeaderSectionHeight } = useAppContext();
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const [buttonSectionHeightRef, setButtonSectionHeightRef] = useState(null);
    const [answers, setAnswers] = useState({});
    const [isVerified, setIsVerified] = useState(false);
    const [isAgreed, setIsAgreed] = useState(false);
    const isMobile = useIsMobile();
    const isTab = useIsTab();
    const isMobileOrTab = isMobile || isTab;
    usePubSub("QNA_ANSWERS", {
        onMessageReceived: ({ message, payload }) => {
            if (message === "QNA_ANSWER") {
                setAnswers((prev) => {
                    return { ...prev, [payload.questionId]: payload }
                })
            }
        },
        onOldMessagesReceived: (messages) => {
            if (!messages?.length) return;
            messages.forEach((msg) => {
                if (msg.message === "QNA_ANSWER") {
                    setAnswers((prev) => {
                        return { ...prev, [msg.payload.questionId]: msg.payload }
                    })
                }
            })
        },
    });

    usePubSub("FACE_VERIFIED", {
        onMessageReceived: ({ message, payload }) => {
            if (message === "Verified") {
                setIsVerified(payload.isVerified);
            }
        },
        onOldMessagesReceived: (messages) => {
            const latestVerifiedMessage = messages
                .filter((msg) => msg.message === "Verified")
                .pop();
            if (latestVerifiedMessage) {
                setIsVerified(latestVerifiedMessage.payload.isVerified);
            }
        },
    });

    usePubSub("AGREED", {
        onMessageReceived: ({ message, payload }) => {
            if (message === "Agreed") {
                setIsAgreed(payload.isAgreed);
            }
        },
        onOldMessagesReceived: (messages) => {
            const latestAgreedMessage = messages
                .filter((msg) => msg.message === "Agreed")
                .pop();
            if (latestAgreedMessage) {
                setIsAgreed(latestAgreedMessage.payload.isAgreed);
            }
        },
    });

    useEffect(() => {
        const handleResize = () => {
            setViewportHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const ButtonSectionHeight = useMemo(
        () => buttonSectionHeightRef?.offsetHeight,
        [buttonSectionHeightRef]
    );

    let spacingHeight = isMobile
        ? HeaderSectionHeight + ButtonSectionHeight + 180 + 70 + 20
        : isTab
            ? HeaderSectionHeight + ButtonSectionHeight + 290 + 70 + 40
            : ButtonSectionHeight + 160 + 56 + 40;

    // Use qna from allPubsubData or fallback to empty array
    const qnaList = allPubsubData?.qna || [];
    const allAnswered = useMemo(() => {
        return qnaList.length > 0 && Object.keys(answers).length === qnaList.length;
    }, [qnaList, answers]);
    const fontFamilyCSS = APP_FONT_STACK;

    const isQuestionEnabled = isVerified && isAgreed;

    return (
        <div className="flex flex-col-reverse lg:flex-row flex-1 py-3 px-3 lg:py-9 lg:px-8 xl:px-14 gap-3 md:gap-4 lg:gap-5 xl:gap-16">
            <MainCard
                formattedTime={formattedTime}
                classNameMain="lg:w-1/2 xl:w-1/2"
                classNameFirstSection="h-full flex flex-col"
                mainTitle={t("QnA")}
                classNameTitleContainer="flex-none"
                FirstSection={
                    <div
                        style={{
                            height: `${viewportHeight - spacingHeight}px`,
                        }}
                        className="mt-3 flex-grow lg:mt-14 px-1 lg:px-0 overflow-y-auto scrollable-content scrollbar scrollbar-primary"
                    >
                        {qnaList.map((item, index) => (
                            <div
                                key={item.id}
                                className="flex flex-col flex-1 mb-4 border-b pb-4 last:border-b-0"
                            >
                                <p
                                    style={{ fontFamily: fontFamilyCSS }}
                                    className={`text-sm md:text-base lg:text-lg font-semibold text-black-150 mb-2 ${!isQuestionEnabled ? "opacity-50" : ""}`}
                                >
                                    {index + 1}. {item.question}
                                </p>
                                <div className="flex gap-4">
                                    {item.answers?.map((answer, ansIndex) => (
                                        <div key={ansIndex} className="flex items-center">
                                            <input
                                                type="radio"
                                                id={`q${item.id}-a${ansIndex}`}
                                                name={`q${item.id}`}
                                                value={answer}
                                                checked={answers[item.id]?.answer === answer}
                                                readOnly
                                                className="w-4 h-4 text-orange-150 border-gray-300 focus:ring-orange-150 pointer-events-none"
                                            />
                                            <label
                                                htmlFor={`q${item.id}-a${ansIndex}`}
                                                style={{ fontFamily: fontFamilyCSS }}
                                                className={`ml-2 text-sm text-gray-700 pointer-events-none ${!isQuestionEnabled ? "opacity-50" : ""}`}
                                            >
                                                {answer}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {qnaList.length === 0 && (
                            <p
                                style={{ fontFamily: fontFamilyCSS }}
                                className="text-center text-gray-500 mt-10"
                            >
                                {t("No questions available.")}
                            </p>
                        )}
                    </div>
                }
                SecondSection={
                    <div
                        ref={setButtonSectionHeightRef}
                        className="flex mt-3 lg:mt-10 px-1 lg:px-0 justify-between"
                    >
                        {/* Display Submit button state if needed, but since it's recording, maybe just show it disabled or not at all? 
                VideoConsentSection shows buttons. Let's show a disabled Submit button to mimic the UI. */}
                        <div className="flex flex-1 text-sm md:text-base">
                            <button
                                style={{ fontFamily: fontFamilyCSS }}
                                className={`bg-orange-150 text-white p-2 md:p-3 rounded-md w-full font-bold lg:max-w-max ${!allAnswered ? "opacity-50 cursor-not-allowed" : ""}`}
                                disabled
                            >
                                {t("Submit")}
                            </button>
                        </div>
                    </div>
                }
            />
            <div className="lg:w-1/2 xl:w-1/2 h-full flex flex-col">
                <RemoteParticipantView />
            </div>
        </div>
    );
};

export default QnaSection;
